import React, { useState } from "react";
import {
  Dialog,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Box,
  Typography,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { createComment, editComment, deleteComment, getComments } from "../../redux/actions/b2bActions";

interface Comment {
  id: number;
  message: string;
  timestamp: string;
  senderName?: string;
  senderId?: number;
}

interface CommentPopupProps {
  open: boolean;
  onClose: () => void;
  jobId?: number;
  receiverId?: number;
}

const defaultMessages = ["Liked it", "Didn't Like", "Consider later", "Fix a meeting"];


const CommentPopup: React.FC<CommentPopupProps> = ({ open, onClose, jobId, receiverId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');
  const isFullScreen = isMobile || isTablet;
  const dispatch = useDispatch();
  
  const [message, setMessage] = useState("");
  const [chat, setChat] = useState<Comment[]>([]);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreComments, setHasMoreComments] = useState(true);

  // Get comments from Redux state
  const commentsData = useSelector((state: any) => state.b2b.comments);

  // Fetch comments function
  const fetchComments = (page = 1, append = false) => {
    if (jobId && receiverId && !loadingComments) {
      setLoadingComments(true);
      dispatch(
        getComments(jobId, receiverId, page, 20)
      );
    }
  };

  // Load comments when popup opens
  React.useEffect(() => {
    if (open && jobId && receiverId) {
      // Clear existing comments immediately when modal opens
      setChat([]);
      setCurrentPage(1);
      setHasMoreComments(true);
      // Set loading state and fetch comments directly
      setLoadingComments(true);
      // Dispatch API call directly since we've already set loading state
      dispatch(
        getComments(jobId, receiverId, 1, 20)
      );
    } else if (!open) {
      // Clear comments when modal closes
      setChat([]);
      setLoadingComments(false);
    }
  }, [open, jobId, receiverId, dispatch]);

  // Update chat when comments data changes
  React.useEffect(() => {
    if (commentsData?.records) {
      const newComments = commentsData.records.map((comment: any) => ({
        id: comment.id,
        message: comment.message,
        timestamp: comment.createdAt || comment.timestamp,
        senderName: comment.senderName || comment.sender?.name || "Unknown User",
        senderId: comment.senderId || comment.sender?.id
      }));
      
      if (currentPage === 1) {
        // First page - reverse the order since API returns newest first, but we want oldest first
        setChat(newComments.reverse());
      } else {
        // Subsequent pages - prepend older comments to the top
        setChat(prev => [...newComments.reverse(), ...prev]);
      }
      
      setHasMoreComments(commentsData.hasNext || false);
      setLoadingComments(false);
    }
  }, [commentsData, currentPage]);

  // Handle scroll to load more comments
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget;
    // Load more comments when scrolling to the top (older comments)
    if (scrollTop === 0 && hasMoreComments && !loadingComments) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchComments(nextPage, true);
    }
  };

  const handleSend = (msg?: string) => {
    const newMsg = msg ?? message;
    if (newMsg.trim() && jobId && receiverId) {
      // Immediately add the message to the UI
      const tempId = Date.now(); // Temporary ID for immediate display
      const newComment: Comment = {
        id: tempId,
        message: newMsg,
        timestamp: new Date().toISOString(),
        senderName: "You", // Current user sending the message
        senderId: undefined // Will be updated when API responds
      };
      
      // Add to chat immediately
      setChat(prev => [...prev, newComment]);
      setMessage("");
      
      // Call API in background without showing loading state
      dispatch(
        createComment(
          jobId,
          newMsg,
          receiverId,
          () => {
            // Success callback - no need to refresh, message is already shown
            if (editIndex !== null) {
              setEditIndex(null);
            }
          },
          () => {
            // Error callback - remove the temporary message and show error
            setChat(prev => prev.filter(comment => comment.id !== tempId));
            // Optionally show error message or revert the UI
          }
        )
      );
    }
  };

  const handleEditComment = (commentId: number, newMessage: string) => {
    if (newMessage.trim() && !loadingEdit) {
      setLoadingEdit(true);
      
      dispatch(
        editComment(
          commentId,
          newMessage,
          () => {
            // Success callback
            setLoadingEdit(false);
            setChat((prev) =>
              prev.map((comment) => 
                comment.id === commentId 
                  ? { ...comment, message: newMessage }
                  : comment
              )
            );
            setEditMessageIndex(null);
            setEditText("");
          },
          () => {
            // Error callback
            setLoadingEdit(false);
          }
        )
      );
    }
  };

  const handleDeleteComment = (commentId: number) => {
    if (!loadingDelete) {
      setLoadingDelete(true);
      
      dispatch(
        deleteComment(
          commentId,
          () => {
            // Success callback
            setLoadingDelete(false);
            setChat((prev) => prev.filter((comment) => comment.id !== commentId));
            setDeleteDialogOpen(false);
            setDeleteMessageIndex(null);
          },
          () => {
            // Error callback
            setLoadingDelete(false);
          }
        )
      );
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return "Just now";
    
    const now = dayjs();
    const commentTime = dayjs(timestamp);
    const diffInMinutes = now.diff(commentTime, 'minute');
    const diffInHours = now.diff(commentTime, 'hour');
    const diffInDays = now.diff(commentTime, 'day');
    
    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} min${diffInMinutes > 1 ? 's' : ''} ago`;
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
    return commentTime.format("MMM D, YYYY");
  };

  const [editMessageIndex, setEditMessageIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  const [deleteMessageIndex, setDeleteMessageIndex] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Edit
  const [editIndex, setEditIndex] = useState<number | null>(null);

  // Delete
  const handleDeleteClick = (index: number) => {
    setDeleteMessageIndex(index);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteMessageIndex !== null && chat[deleteMessageIndex]) {
      const commentToDelete = chat[deleteMessageIndex];
      handleDeleteComment(commentToDelete.id);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteMessageIndex(null);
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // const handleOptionClick = (option: string) => {
  //   console.log("Selected:", option);
  //   handleClose();
  // };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isFullScreen}
      maxWidth={isFullScreen ? false : "sm"}
      fullWidth={!isFullScreen}
      PaperProps={{
        sx: {
          borderRadius: isFullScreen ? 0 : "18px",
          display: "flex",
          flexDirection: "column",
          width: isFullScreen ? "100vw" : { xs: "90vw", sm: "500px", md: "519px" },
          height: isFullScreen ? "100vh" : { xs: "80vh", sm: "580px", md: "610px" },
          maxWidth: isFullScreen ? "none" : "519px",
          maxHeight: isFullScreen ? "none" : "610px",
          p: 0,
          margin: 0,
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: isFullScreen ? "none" : "blur(6px)",
          backgroundColor: isFullScreen ? "transparent" : "rgba(0, 0, 0, 0.59)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: isFullScreen ? 3 : 2,
          py: isFullScreen ? 2 : 1,
          borderBottom: isFullScreen ? "1px solid #E0E0E0" : "none",
          backgroundColor: "white",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {isFullScreen && (
            <IconButton 
              onClick={onClose}
              sx={{ 
                padding: "8px",
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: "#E0E0E0",
                }
              }}
            >
              <Close sx={{ color: "black", fontSize: "20px" }} />
            </IconButton>
          )}
          <Typography 
            variant="h6" 
            fontFamily={"DM Sans"} 
            fontWeight={500}
            sx={{ fontSize: isFullScreen ? "20px" : "18px" }}
          >
            Comment
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button 
            variant="text"
            onClick={() => setChat([])}
            sx={{
              background: "none",
              border: "none",
              boxShadow: "none",
              color: "#777777",
              textTransform: "none",
              fontFamily: "DM Sans",
              fontSize: isFullScreen ? "15px" : "14px",
              fontWeight: 400,
              padding: isFullScreen ? "8px 12px" : "4px 8px",
              "&:hover": {
                background: "none",
                color: "#555",
              },
            }}
          >
            Clear Comment
          </Button>
          {!isFullScreen && (
            <IconButton onClick={onClose}>
              <Close sx={{ color: "black" }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Chat Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: isFullScreen ? 3 : 2,
          display: "flex",
          flexDirection: "column",
          gap: isFullScreen ? 2 : 1,
          bgcolor: "#fafafa",
          minHeight: 0, // Important for flex behavior
        }}
        onScroll={handleScroll}
      >
        {/* Loading indicator for older comments */}
        {loadingComments && currentPage > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} sx={{ color: '#756DE9' }} />
            <Typography sx={{ ml: 1, fontSize: '14px', color: '#666' }}>
              Loading older comments...
            </Typography>
          </Box>
        )}
        
        {chat.length === 0 && !loadingComments ? (
          <Typography
            variant="body2"
            sx={{ color: "gray", textAlign: "center", mt: 5 }}
          >
            No comments yet.
          </Typography>
        ) : chat.length === 0 && loadingComments ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
            <CircularProgress size={24} sx={{ color: '#756DE9' }} />
            <Typography sx={{ ml: 2, fontSize: '14px', color: '#666' }}>
              Loading comments...
            </Typography>
          </Box>
        ) : (
          chat.map((comment, i) => (
            <Paper
              key={comment.id}
              sx={{
                p: isFullScreen ? 2 : 1,
                Width: "100%",
                // alignSelf: "flex-end",
                bgcolor: "#F5F6FA",
                borderRadius: "6px 6px 6px 0px",
                fontFamily: "DM Sans",
                fontSize: isFullScreen ? "15px" : "14px"
              }}
              elevation={0}
            >

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1, mr: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography sx={{ 
                      fontFamily: "DM Sans", 
                      fontSize: "14px", 
                      color: "#756DE9",
                      fontWeight: "500"
                    }}>
                      {comment.senderName || "Unknown User"}
                    </Typography>
                    <Typography sx={{ 
                      fontFamily: "DM Sans", 
                      fontSize: "12px", 
                      color: "#888", 
                      whiteSpace: "nowrap"
                    }}>
                      {formatTimestamp(comment.timestamp)}
                    </Typography>
                  </Box>
                  
                  <Box>
                    {editMessageIndex === i ? (
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          value={editText}
                          onChange={e => setEditText(e.target.value)}
                          size="small"
                          fullWidth
                          sx={{ mb: 1 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={() => {
                              setEditMessageIndex(null);
                              setEditText("");
                            }}
                            sx={{ 
                              color: '#666',
                              textTransform: 'none',
                              fontSize: '12px'
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={loadingEdit || !editText.trim()}
                            onClick={() => handleEditComment(comment.id, editText)}
                            sx={{ 
                              backgroundColor: '#756DE9',
                              textTransform: 'none',
                              fontSize: '12px',
                              minWidth: '60px'
                            }}
                          >
                            {loadingEdit ? (
                              <CircularProgress size={12} sx={{ color: '#fff' }} />
                            ) : (
                              'Save'
                            )}
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Typography sx={{ 
                        fontFamily: "DM Sans", 
                        fontSize: "14px", 
                        color: "#222",
                        lineHeight: 1.4,
                        wordBreak: "break-word"
                      }}>
                        {comment.message}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* More icon button */}
                <Box sx={{ display: "flex", alignItems: "flex-start", ml: 1 }}>
                  <IconButton
                    size="small"
                    aria-controls={open ? "dropdown-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    sx={{
                      padding: "4px",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      }
                    }}
                  >
                    <img
                      src="/images/browse-jobs/verticalmenu.svg"
                      width="16px"
                      height="16px"
                      alt="More options"
                    />
                  </IconButton>

                  <Menu
                    id="dropdown-menu"
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    sx={{ 
                      borderRadius: "10px",
                      "& .MuiPaper-root": {
                        borderRadius: "10px",
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)"
                      }
                    }}
                  >
                    <MenuItem 
                      onClick={() => handleDeleteClick(i)}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#F5F6FA",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: "32px" }}>
                        <img 
                          src="/images/browse-jobs/deleteicon.svg" 
                          width="16px" 
                          height="16px"
                          alt="Delete"
                        />
                      </ListItemIcon>
                      <Typography variant="body2">Delete</Typography>
                    </MenuItem>
                    
                    <MenuItem
                      onClick={() => {
                        setEditText(comment.message);
                        setEditMessageIndex(i);
                        handleClose();
                      }}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#F5F6FA",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: "32px" }}>
                        <img 
                          src="/images/browse-jobs/editicon.svg" 
                          width="16px" 
                          height="16px"
                          alt="Edit"
                        />
                      </ListItemIcon>
                      <Typography variant="body2">Edit</Typography>
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>
            </Paper>
          ))
        )}
      </Box>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}
        PaperProps={{
          sx: {

            borderRadius: "18px",
            paddingBottom: 1,
          },
        }}>
        <DialogTitle display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>Confrim <IconButton onClick={handleDeleteCancel}>
          <Close sx={{ color: "black" }} />
        </IconButton>
        </DialogTitle>
        <DialogContent>
          Are you sure you want to delete the comment?
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-start", pl: 3, gap: 2 }}>
          <Button 
            variant="contained" 
            onClick={handleDeleteConfirm}
            disabled={loadingDelete}
            sx={{
              backgroundColor: "#756DE9",
              width: "126px",
              height: "48px",
              borderRadius: "95px",
              textTransform: "none",
              opacity: loadingDelete ? 0.7 : 1,
              ":hover": { backgroundColor: "#7c75e8ff" },
              "&:disabled": {
                backgroundColor: "#756DE9",
                color: "#fff",
              },
            }}
          >
            {loadingDelete ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: '#fff' }} />
                <Typography sx={{ fontSize: '14px' }}>Deleting...</Typography>
              </Box>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Input Section */}
      <Box
        sx={{
          borderTop: "1px solid #ddd",
          p: isFullScreen ? 3 : 1.5,
          display: "flex",
          flexDirection: "column",
          gap: isFullScreen ? 2 : 1,
          bgcolor: "white",
          position: "sticky",
          bottom: 0,
          zIndex: 1,
        }}
      >

        <TextField
          fullWidth
          placeholder="Start Typing"
          size={isFullScreen ? "medium" : "small"}
          sx={{ fontWeight: "200" }}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton 
                  color="primary" 
                  onClick={() => handleSend()}
                  disabled={!message.trim() || !jobId || !receiverId}
                  sx={{
                    padding: isFullScreen ? "12px" : "8px",
                    "&:hover": {
                      backgroundColor: "rgba(75, 68, 212, 0.1)",
                    },
                    "&:disabled": {
                      opacity: 0.5,
                    }
                  }}
                >
                  <img src="/images/browse-jobs/sendbutton.svg"
                    width={isFullScreen ? "24px" : "20px"}
                    height={isFullScreen ? "24px" : "20px"}
                  />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              borderRadius: "999px",
              backgroundColor: "rgba(75, 68, 212, 0.02)",
              boxShadow: "inset 0px 0px 11px 2px rgba(75, 68, 212, 0.08)",
              fontFamily: "DM Sans",
              fontSize: isFullScreen ? "16px" : "14px",
              fontWeight: 400,
              "& .MuiOutlinedInput-root": {
                borderRadius: "999px",
                "& fieldset": {
                  border: "none",
                },
              },
              px: isFullScreen ? 3 : 2,
              py: isFullScreen ? 1 : 0.5,
            },
          }}
        />


        <Box display="flex" flexWrap="wrap" gap={isFullScreen ? 1.5 : 1}>
          {defaultMessages.map((msg, i) => (
            <Chip
              key={i}
              label={msg}
              variant="outlined"
              onClick={() => handleSend(msg)}
              sx={{ 
                borderRadius: "16px", 
                backgroundColor: "#F5F6FA", 
                border: "1px dashed #B7B3EB", 
                height: isFullScreen ? "36px" : "30px",
                fontSize: isFullScreen ? "14px" : "12px",
                "&:hover": {
                  backgroundColor: "#E8E4FF",
                  border: "1px dashed #756DE9",
                }
              }}
            />
          ))}
        </Box>
      </Box>
    </Dialog>
  );
};

export default CommentPopup;
